// @ts-nocheck
import { getStaffPermissions } from '@/lib/staffPermissions';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { QRScannerV2 } from '@/components/staff/QRScannerV2';
import { Shield, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

const StaffCheckInPage: React.FC = () => {
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStaffEvents = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        // Find staff member by email
        const { data: staffData, error: staffError } = await supabase
          .from('event_staff')
          .select('id, is_active')
          .eq('email', user.email)
          .eq('is_active', true)
          .single();

        if (staffError || !staffData) {
          console.error('Staff member not found or inactive');
          setIsLoading(false);
          return;
        }

        // Fetch events where staff has check_in or all permissions
        const { data: assignmentData, error: assignmentError } = await supabase
          .from('event_staff_assignments')
          .select(`
            event_id,
            permissions,
            role,
            events:event_id (
              id,
              name,
              start_date,
              end_date
            )
          `)
          .eq('staff_id', staffData.id);

        if (assignmentError) throw assignmentError;

        // Filter events where permissions include check_in or all
        const filteredEvents = (assignmentData || [])
          .filter((assignment: any) => {
            return getStaffPermissions(assignment).canCheckIn;
          })
          .map((assignment: any) => assignment.events)
          .filter(Boolean);

        setEvents(filteredEvents);
        const urlEvent = searchParams.get('event');
        if (urlEvent && filteredEvents.some((e: any) => e.id === urlEvent)) {
          setSelectedEvent(urlEvent);
        } else if (filteredEvents.length === 1) {
          setSelectedEvent(filteredEvents[0].id);
        }
      } catch (error) {
        console.error('Error fetching staff events:', error);
        toast.error('Failed to load assigned events');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStaffEvents();
  }, [user]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Events Assigned</h3>
              <p className="text-muted-foreground max-w-md">
                You don't have any events assigned with check-in permissions. 
                Please contact your administrator to get access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">
            Event Check-In Station
          </h1>
        </div>
        <p className="text-gray-600">
          Scan QR codes to check in registrations, redeem spectator tickets, and validate extras
        </p>
      </div>

      {/* Event Selector */}
      {events.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Event</CardTitle>
            <CardDescription>Choose which event you're checking in for</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="event-select">Event</Label>
              <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                <SelectTrigger id="event-select">
                  <SelectValue placeholder="Select an event..." />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {format(parseLocalDate(event.start_date), 'MMM d, yyyy')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedEvent && events.length > 1 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Please select an event above to begin check-in
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Staff Instructions</CardTitle>
              <CardDescription>How to use the check-in scanner</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">1</div>
                <div>
                  <p className="font-medium">Choose scanning mode</p>
                  <p className="text-muted-foreground">Use Camera mode for physical QR codes or Manual mode to type codes</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">2</div>
                <div>
                  <p className="font-medium">Scan or enter QR code</p>
                  <p className="text-muted-foreground">Point camera at attendee's QR code or manually type the code value</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">3</div>
                <div>
                  <p className="font-medium">Verify result</p>
                  <p className="text-muted-foreground">Green checkmark = success, Red X = already used or invalid</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {selectedEvent && <QRScannerV2 />}
        </>
      )}
    </div>
  );
};


export default StaffCheckInPage;
